import React from "react";
import { Box, SxProps, Theme } from "@mui/material";

interface CornerRibbonProps {
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  children: React.ReactNode;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  fontColor?: string;
  backgroundColor?: string;
  containerStyle?: React.CSSProperties;
  containerSx?: SxProps<Theme>;
  className?: string;
}

const CornerRibbon = ({
  position = "top-right",
  children,
  style,
  sx,
  fontColor = "#f0f0f0",
  backgroundColor = "#2c7",
  containerStyle: userContainerStyle,
  containerSx,
  className,
  ...rest
}: CornerRibbonProps) => {
  const positionStyle = React.useMemo(() => {
    let _style: React.CSSProperties = {};
    switch (position) {
      case "top-left":
        _style = {
          top: 0,
          left: 0,
          transform:
            "translateY(-100%) rotate(-90deg) translateX(-70.71067811865476%) rotate(45deg)",
          transformOrigin: "bottom left",
          WebkitTransform:
            "translateY(-100%) rotate(-90deg) translateX(-70.71067811865476%) rotate(45deg)",
        };
        break;
      case "top-right":
        _style = {
          top: 0,
          right: 0,
          transform:
            "translateY(-100%) rotate(90deg) translateX(70.71067811865476%) rotate(-45deg)",
          transformOrigin: "bottom right",
          WebkitTransform:
            "translateY(-100%) rotate(90deg) translateX(70.71067811865476%) rotate(-45deg)",
        };
        break;
      case "bottom-left":
        _style = {
          bottom: 0,
          left: 0,
          transform:
            "translateY(100%) rotate(90deg) translateX(-70.71067811865476%) rotate(-45deg)",
          transformOrigin: "top left",
          WebkitTransform:
            "translateY(100%) rotate(90deg) translateX(-70.71067811865476%) rotate(-45deg)",
        };
        break;
      case "bottom-right":
        _style = {
          right: 0,
          bottom: 0,
          transform:
            "translateY(100%) rotate(-90deg) translateX(70.71067811865476%) rotate(45deg)",
          transformOrigin: "top right",
          WebkitTransform:
            "translateY(100%) rotate(-90deg) translateX(70.71067811865476%) rotate(45deg)",
        };
        break;
      default:
        break;
    }
    return _style;
  }, [position]);

  const baseSx: SxProps<Theme> = React.useMemo(
    () => ({
      position: "absolute",
      padding: "0.1em 2em",
      zIndex: 99,
      textAlign: "center",
      letterSpacing: "2px",
      fontSize: "12px",
      pointerEvents: "auto",
      boxShadow: "0 0 3px rgba(0,0,0,.3)",
      ...(backgroundColor && { backgroundColor }),
      ...(fontColor && { color: fontColor }),
      ...positionStyle,
    }),
    [backgroundColor, fontColor, positionStyle]
  );

  const containerBaseSx: SxProps<Theme> = React.useMemo(
    () => ({
      position: "absolute",
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
      overflow: "hidden",
      backgroundColor: "transparent",
      pointerEvents: "none",
    }),
    []
  );

  const combinedContainerSx: SxProps<Theme> = React.useMemo(
    () => (containerSx ? ([containerBaseSx, containerSx] as SxProps<Theme>) : containerBaseSx),
    [containerBaseSx, containerSx]
  );

  const combinedSx: SxProps<Theme> = React.useMemo(
    () => (sx ? ([baseSx, sx] as SxProps<Theme>) : baseSx),
    [baseSx, sx]
  );

  return (
    <Box
      sx={combinedContainerSx}
      style={userContainerStyle}
      className={className}
      {...rest}
    >
      <Box sx={combinedSx} style={style}>
        {children}
      </Box>
    </Box>
  );
};
export default CornerRibbon;
